import { Fragment, useState } from 'react';
import { ErrorText } from '../../components/ErrorText';
import { FloatingSelectField } from '../../components/FloatingSelectField';
import { InfoNote } from '../../components/InfoNote';
import { SectionHeader } from '../../components/SectionHeader';
import { SelectableOptionCard } from '../../components/SelectableOptionCard';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { SubSelectCard } from '../../components/SubSelectCard';
import { ErrorIcon } from '../../icons/ErrorIcon';
import {
  Fields,
  OptionsSection,
  Panel,
  SectionError,
  SelectionCount,
  NoticeSlot,
} from './PackageInclusionChoicePanel.styles';
import type { PackageInclusionChoicePanelProps } from './PackageInclusionChoicePanel.types';
import { radius } from '../../theme/tokens';

export function PackageInclusionChoicePanel({
  options,
  selectedOptionIds,
  maximumSelections,
  selectionCountText,
  optionError,
  onOptionToggle,
  onDayChange,
  onTimeChange,
  labels = {},
}: PackageInclusionChoicePanelProps) {
  const {
    title = 'Inclusions.',
    subtitle = 'Choose one option, and your preferred time.',
    timeslotHeading = 'When would you like?',
    dayLabel = 'Select day',
    timeLabel = 'Select time',
    noTimeslots = 'There are no time slots available.',
    noteDismissLabel,
  } = labels;

  const [dismissedNotes, setDismissedNotes] = useState<string[]>([]);

  // A one-option group swaps on click, so only cap groups that take several.
  const atMax =
    maximumSelections !== undefined &&
    maximumSelections > 1 &&
    selectedOptionIds.length >= maximumSelections;

  return (
    <Panel data-testid="package-inclusion-choice-panel">
      <SectionHeader title={title} subtitle={subtitle} />
      {selectionCountText && (
        <SelectionCount aria-live="polite">{selectionCountText}</SelectionCount>
      )}
      {optionError && (
        <SectionError role="alert" data-error="true" aria-invalid="true" tabIndex={-1}>
          <ErrorText>{optionError}</ErrorText>
        </SectionError>
      )}
      <OptionsSection>
        {options.map((option) => {
          const isSelected = selectedOptionIds.includes(option.id);
          const isDisabled = Boolean(option.disabled) || (atMax && !isSelected);
          const noTimes = !option.isLoading && option.timeOptions.length === 0;

          return (
            <Fragment key={option.id}>
              <SelectableOptionCard
                label={option.label}
                isSelected={isSelected}
                disabled={isDisabled}
                imageUrl={option.imageUrl}
                imageAlt={option.imageAlt}
                onSelect={() => onOptionToggle(option.id)}
              />
              {isSelected && (
                <SubSelectCard heading={timeslotHeading}>
                  <Fields>
                    <FloatingSelectField
                      label={dayLabel}
                      options={option.dayOptions ?? []}
                      value={option.selectedDay}
                      onChange={(value) => onDayChange(option.id, value)}
                      error={option.dayError}
                      fixedValueLabel={option.fixedDayLabel}
                      aria-label={`${dayLabel} ${option.label}`}
                    />
                    {option.isLoading ? (
                      <SkeletonBlock height="72px" borderRadius={radius('card', 'radius-md')} />
                    ) : (
                      <FloatingSelectField
                        label={timeLabel}
                        options={option.timeOptions}
                        value={option.selectedTime}
                        onChange={(value) => onTimeChange(option.id, value)}
                        error={noTimes ? undefined : option.timeError}
                        disabled={noTimes}
                        aria-label={`${timeLabel} ${option.label}`}
                      />
                    )}
                  </Fields>
                  {noTimes && (
                    <NoticeSlot
                      data-error={option.timeError ? 'true' : undefined}
                      aria-invalid={option.timeError ? true : undefined}
                      tabIndex={option.timeError ? -1 : undefined}
                    >
                      <InfoNote
                        variant={option.timeError ? 'danger' : 'warning'}
                        icon={<ErrorIcon size={19} />}
                        text={noTimeslots}
                      />
                    </NoticeSlot>
                  )}
                  {!noTimes && option.noteText && !dismissedNotes.includes(option.id) && (
                    <NoticeSlot>
                      <InfoNote
                        text={option.noteText}
                        onDismiss={() => setDismissedNotes((current) => [...current, option.id])}
                        dismissLabel={noteDismissLabel}
                      />
                    </NoticeSlot>
                  )}
                </SubSelectCard>
              )}
            </Fragment>
          );
        })}
      </OptionsSection>
    </Panel>
  );
}
